import {
  FC,
  PropsWithChildren,
  createContext,
  useState,
  useCallback,
  useContext,
  useEffect,
} from 'react';
import { To, NavigateOptions, useNavigate, useLocation } from 'react-router-dom';
import noop from 'lodash/noop';


type BlockerBlocked = { state: 'blocked', reset(): void, proceed(): void }
type BlockerUnblocked = { state: 'unblocked', reset: undefined, proceed: undefined }
type BlockerProceeding = { state: 'proceeding', reset: undefined, proceed: undefined }
type Blocker = BlockerUnblocked | BlockerBlocked | BlockerProceeding;

type ContextValue = {
  blocker: Blocker
  setBlocked: (nextBlocked: boolean) => void
  blockedNavigate: (to: To, options?: NavigateOptions) => void
}

const UNBLOCKER_BLOCKER: BlockerUnblocked = {
  state: 'unblocked',
  reset: undefined,
  proceed: undefined,
};

const NavigationContext = createContext<ContextValue>({
  blocker: UNBLOCKER_BLOCKER,
  setBlocked: noop,
  blockedNavigate: noop,
});

export const NavigationProvider: FC<PropsWithChildren> = ({ children }) => {
  const [blocker, setBlocker] = useState<Blocker>(UNBLOCKER_BLOCKER);
  const [isBlocked, setBlocked] = useState(false);

  const location = useLocation();

  const unblock = useCallback(() => {
    setBlocker(UNBLOCKER_BLOCKER);
    setBlocked(false);
  }, []);

  useEffect(() => {
    return unblock;
    // skipped intentionally. Clean up blocker when location is changed
    //  eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  const navigate = useNavigate();

  const blockedNavigate = useCallback((to: To, options?: NavigateOptions) => {
    if (blocker.state === 'blocked') {
      return;
    }

    if (isBlocked && blocker.state === 'unblocked') {
      setBlocker({
        state: 'blocked',
        proceed: () => navigate(to, options),
        reset: () => setBlocker(UNBLOCKER_BLOCKER),
      });

      return;
    }

    navigate(to, options);
  }, [navigate, blocker, isBlocked]);

  return (
    <NavigationContext.Provider value={{ blockedNavigate, blocker, setBlocked }}>
      {children}
    </NavigationContext.Provider>
  );
};

export const useBlockedNavigate = () => {
  return useContext(NavigationContext).blockedNavigate;
};

type BlockerFunction = () => boolean
export type ShouldBlock = boolean | BlockerFunction

export const useBlocker = (shouldBlock: ShouldBlock): Blocker => {
  const { blocker, setBlocked } = useContext(NavigationContext);

  useEffect(() => {
    const isBlocked = typeof shouldBlock === 'function' ? shouldBlock() : shouldBlock;

    setBlocked(isBlocked);
  }, [shouldBlock, setBlocked]);

  return blocker;
};
